import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { put } from '@nineeng/request';
import Typography from '@mui/material/Typography';
import { DetailsTab, Spinner, Badge, RequirementChip } from '@repo/components';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import { getEvent } from 'src/api/personal/events';
import { getEventForm } from 'src/api/personal/eventDetail';
import { getEventUsers } from 'src/api/personal/eventDetail';
import { PERMISSIONS } from '@repo/shared/domain';
import type { User } from '@repo/shared/domain';

export const Route = createFileRoute('/personal_/events/$eventId/details')({
	component: EventDetailsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		lang: (search.lang as string) || 'en',
	}),
});

function EventDetailsPage() {
	const { eventId } = Route.useParams();
	const { lang } = Route.useSearch();
	const queryClient = useQueryClient();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['personal-event', eventId, !hasAllEventsView],
		queryFn: () => getEvent(eventId, !hasAllEventsView),
	});

	const { data: eventForm, isLoading: formLoading } = useQuery({
		queryKey: ['personal-event-form', event?.type?.Form?.slug, lang],
		queryFn: () =>
			getEventForm(
				event?.type?.Form?.slug || '',
				constructQuery({ lang }),
			),
		enabled: !!event?.type?.Form?.slug,
	});

	const { data: usersResponse } = useQuery({
		queryKey: ['personal-event-users', eventId, !hasAllEventsView],
		queryFn: () => getEventUsers(eventId, !hasAllEventsView),
		enabled: !!event,
	});

	const users = (usersResponse?.data || []).filter((u: User) => u.status === 'active');

	if (userLoading || eventLoading || formLoading) {
		return (
			<div className="flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!event) return null;

	const isOwnEvent = event.adminIds?.includes(authenticatedUser?.id || '');

	const printHeader = (
		<div className="flex gap-2 p-4">
			<div className="w-[300px] flex items-center">
				<img style={{ objectFit: 'contain', maxHeight: 70, width: '100%' }} src="/api/public/logo" alt="Logo" />
			</div>
			<div className="flex-grow flex flex-row flex-wrap p-2 gap-4">
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Event Name
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.name || '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Type
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.type ? (
							<RequirementChip object={{ status: event.status, type: { name: event.type?.name || '' } }} />
						) : (
							'-'
						)}
					</Typography>
				</div>
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Start Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.startTime ? new Date(event.startTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Finish Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.finishTime ? new Date(event.finishTime).toLocaleString('en-GB') : '-'}
					</Typography>
				</div>
				<div className="w-[40%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Users
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{users?.length
							? users.map((r) => (
									<Badge key={r.email}>
										{r.firstName} {r.lastName}
									</Badge>
								))
							: '-'}
					</div>
				</div>
			</div>
		</div>
	);

	const handleDetailsUpdate = async (eventData: Record<string, unknown>) => {
		const prefix = isOwnEvent ? '/api/personal-dashboard/own-events' : '/api/personal-dashboard/events';
		try {
			await put(`${prefix}/${eventId}/details`, { ...eventData });
			EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Error saving' });
		}
	};

	const canEdit =
		verifyPermission([PERMISSIONS.personalAllEventsEdit]) ||
		(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwnEvent);

	return (
		<div className="px-4 pt-4">
			<DetailsTab
				data={event?.eventData || []}
				printHeader={printHeader}
				form={eventForm}
				onLanguageChange={() => {}}
				language={lang}
				availableLanguages={eventForm?.availableLanguages ?? [lang]}
				onSave={handleDetailsUpdate}
				readOnly={!canEdit}
			/>
		</div>
	);
}
